import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  BookOpen,
  Flame,
  Lock,
  Mic,
  Shield,
  Users,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { PRICING_PREVIEW_PLANS } from '../../content/publicSiteContent';
import { HomePainPoints } from '../home/HomePainPoints';
import { HomepageDemoTeaser } from './productDemo';

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-48px' },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
};

const shell = 'mx-auto max-w-6xl px-4 sm:px-6 lg:px-8';
const card =
  'rounded-2xl border border-[hsl(38_16%_82%)] bg-[hsl(42_28%_99%)] shadow-[0_12px_40px_-24px_hsl(28_14%_18%_/_0.12)]';
const primaryBtn =
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-[hsl(152_28%_22%)] px-6 text-sm font-semibold text-[hsl(42_30%_96%)] transition hover:bg-[hsl(152_28%_18%)]';
const outlineBtn =
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-[hsl(38_16%_78%)] bg-white px-6 text-sm font-medium text-[hsl(24_14%_16%)] transition hover:border-[hsl(38_36%_42%)]';

const TRUST_BADGES = [
  { icon: Shield, label: 'Private by default' },
  { icon: Users, label: 'Invite-only family access' },
  { icon: Lock, label: 'Your data belongs to you' },
  { icon: Mic, label: 'Voice memories stay preserved' },
  { icon: Users, label: 'Built for families, not public social posting' },
];

const SIMPLE_STEPS = [
  {
    step: '01',
    label: 'Create your archive',
    detail: 'Name your private family space and add the first people you wish to honour.',
  },
  {
    step: '02',
    label: 'Record stories',
    detail: 'Write or speak, and attach memories to the people they belong to.',
  },
  {
    step: '03',
    label: 'Save family documents',
    detail: 'Photos, letters, and records linked to names, not lost in folders.',
  },
  {
    step: '04',
    label: 'Invite family',
    detail: 'Share privately with relatives who should see or help preserve.',
  },
];

const PRODUCT_PILLARS = [
  {
    icon: BookOpen,
    title: 'AI Story Studio',
    copy: 'Turn voice, text and prompts into chaptered life stories and Legacy Books.',
    to: '/story-studio',
    cta: 'Open Story Studio',
  },
  {
    icon: Lock,
    title: 'Family Handover Vault',
    copy: 'Keep important contacts, instructions and document locations private and organised.',
    to: '/vault',
    cta: 'Open Vault',
  },
  {
    icon: Flame,
    title: 'Memorial Bridge',
    copy: 'Create a respectful tribute page with stories, tributes, QR links and keepsakes when the time is right.',
    to: '/memorials',
    cta: 'Explore Memorial Bridge',
  },
];

function SectionHeading({ eyebrow, title, className }) {
  return (
    <div className={cn('max-w-2xl', className)}>
      {eyebrow ? (
        <p className="mb-2 text-[0.68rem] font-semibold uppercase tracking-[0.2em] text-[hsl(38_36%_42%)]">
          {eyebrow}
        </p>
      ) : null}
      <h2 className="font-serif text-2xl font-light leading-snug text-[hsl(24_14%_16%)] sm:text-3xl">{title}</h2>
    </div>
  );
}

export function LandingBelowHero({ openCreateDialog }) {
  return (
    <>
      {/* Trust badge row */}
      <section className="border-b border-[hsl(38_16%_82%)] bg-[hsl(42_28%_98%)] py-8 md:py-10">
        <div className={cn(shell, 'flex flex-wrap items-center justify-center gap-x-6 gap-y-4 md:gap-x-10')}>
          {TRUST_BADGES.map(({ icon: Icon, label }) => (
            <span
              key={label}
              className="inline-flex max-w-[14rem] items-center gap-2.5 text-center text-sm text-[hsl(32_10%_38%)] sm:max-w-none sm:text-left"
            >
              <span
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[hsl(43_48%_58%_/_0.28)] bg-[hsl(42_28%_99%)] text-[hsl(38_36%_42%)]"
                aria-hidden
              >
                <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
              </span>
              {label}
            </span>
          ))}
        </div>
      </section>

      {/* Simple steps */}
      <section id="getting-started" className="scroll-mt-24 bg-[hsl(42_28%_98%)] py-16 md:py-24">
        <div className={shell}>
          <motion.div {...fadeUp} className="mx-auto max-w-2xl text-center">
            <SectionHeading eyebrow="Simple steps" title="Start simply. Grow over time." />
            <p className="mt-4 text-sm leading-relaxed text-[hsl(32_10%_38%)] sm:text-base">
              Add one person, one story, or one document, then invite family when you are ready.
            </p>
          </motion.div>
          <ol className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {SIMPLE_STEPS.map((item, i) => (
              <motion.li
                key={item.label}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className={cn(card, 'list-none p-6')}
              >
                <span className="font-serif text-2xl text-[hsl(43_48%_58%)]">{item.step}</span>
                <p className="mt-4 font-medium text-[hsl(24_14%_16%)]">{item.label}</p>
                <p className="mt-2 text-sm leading-relaxed text-[hsl(32_10%_38%)]">{item.detail}</p>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      <HomePainPoints openCreateDialog={openCreateDialog} />

      {/* Three product pillars */}
      <section id="pillars" className="scroll-mt-24 border-y border-[hsl(38_16%_82%)] bg-[hsl(42_28%_96%)] py-14 md:py-20">
        <div className={shell}>
          <motion.div {...fadeUp} className="mx-auto max-w-2xl text-center">
            <SectionHeading
              eyebrow="Core products"
              title="Three ways MyLegacyBridge protects family memory"
              className="mx-auto"
            />
          </motion.div>
          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {PRODUCT_PILLARS.map((pillar, i) => {
              const Icon = pillar.icon;
              return (
                <motion.article
                  key={pillar.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className={cn(card, 'flex flex-col p-6')}
                  data-testid={`homepage-pillar-${pillar.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[hsl(38_16%_82%)] bg-[hsl(43_32%_98%)] text-[hsl(38_36%_42%)]">
                    <Icon className="h-5 w-5" strokeWidth={1.35} aria-hidden />
                  </span>
                  <h3 className="mt-4 font-serif text-xl text-[hsl(24_14%_16%)]">{pillar.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-[hsl(32_10%_38%)]">{pillar.copy}</p>
                  <Link
                    to={pillar.to}
                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(152_28%_22%)] hover:underline"
                  >
                    {pillar.cta}
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                  </Link>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Demo teaser */}
      <HomepageDemoTeaser />

      {/* Emotional statement */}
      <section className="bg-[hsl(24_14%_10%)] py-14 md:py-20">
        <div className={cn(shell, 'max-w-4xl text-center')}>
          <motion.blockquote {...fadeUp}>
            <p className="font-serif text-2xl font-light leading-snug text-[#faf7f2] sm:text-3xl md:text-4xl">
              The stories you save today become the legacy your family keeps tomorrow.
            </p>
          </motion.blockquote>
        </div>
      </section>

      {/* Pricing preview */}
      <section id="pricing-preview" className="scroll-mt-24 bg-[hsl(42_28%_98%)] py-14 md:py-18">
        <div className={cn(shell, 'max-w-4xl')}>
          <motion.div {...fadeUp} className="text-center">
            <SectionHeading eyebrow="Pricing" title="Start free. Upgrade when your archive grows." className="mx-auto" />
          </motion.div>
          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {PRICING_PREVIEW_PLANS.map((plan, i) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className={cn(
                  card,
                  'p-5 text-center',
                  plan.highlight && 'border-[hsl(43_48%_58%_/_0.45)] ring-1 ring-[hsl(43_48%_58%_/_0.2)]',
                )}
              >
                <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{plan.name}</p>
                <p className="mt-2 font-serif text-2xl text-[hsl(24_14%_16%)]">{plan.price}</p>
                <p className="mt-1 text-xs text-muted-foreground">{plan.note}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-8 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center sm:justify-center">
            <button type="button" onClick={openCreateDialog} className={primaryBtn}>
              Start your archive
              <ArrowRight className="h-4 w-4" aria-hidden />
            </button>
            <Link to="/pricing" className={outlineBtn}>
              View pricing
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

export default LandingBelowHero;
